import './Timeline.css';
import type { JSX } from 'preact';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { focusIfKeyboard } from '../../a11y/focus';
import { t } from '../../copy/strings';
import { fmtClockSec, fmtHourMark, fmtTime } from '../../format/time';
import { T_OPEN } from '../../model/time';
import { goLive, scrubTo } from '../../state/actions';
import { useConfig, useLayout, useNow, useTimeline, useView } from '../../state/hooks';
import { GoLiveButton } from './GoLiveButton';
import { LiveChip } from './LiveChip';
import {
  availTMin,
  type Gesture,
  gestureTarget,
  hourMarks,
  keyAction,
  keyGesture,
  type MarkLevel,
  markLevel,
  minuteAtFraction,
  pos,
  sliderInert,
  type TimelineClock,
  type TrackWindow,
  trackWindow,
} from './window';

/**
 * No máximo 4 pedidos de replay por segundo no arraste e nas teclas repetidas: cada `scrubTo` grava o `t` com
 * `replaceState` (≤ 4 escritas/s, §5.2.6; o Safari recusa mais de 100 por 30 s). O primeiro pedido sai na hora; os
 * seguintes se fundem e o último vence (o soltar do ponteiro manda o que falta na hora).
 */
export const SCRUB_MIN_INTERVAL_MS = 250;
/** Toque: vira arraste horizontal com |dx| > 8 px e |dx| > |dy| (§10.4.2). */
export const TOUCH_DRAG_PX = 8;
/** Toque: tocar e soltar a menos de 10 px, sem arraste, vai ao ponto tocado (§10.4.2). */
export const TAP_MAX_PX = 10;

/** A entrada (§10.2 bloco 5) só na primeira montagem da página: trocar de modo (girar o celular) não a repete. */
let introShown = false;
/** Testes: a próxima montagem volta a ter a entrada. */
export function resetTimelineIntro(): void {
  introShown = false;
}

export interface TimelineProps {
  /**
   * `completeAtTMin` de §10.2: t_min do 1º ponto do histórico nacional com 100% (`completeAtFromHistory` de
   * window.ts sobre `historySeries`), que congela o fim da trilha (M15). Quem monta passa (o slot lê o histórico, que
   * `features/` não lê). Ausente: o componente usa o 1º instante ao vivo em que viu o Brasil em 100%.
   */
  completeAtTMin?: number | null;
  /**
   * t_min do 1º item do `ArchiveIndex` (§10.2, §10.4.1): `Home` vai até ele; null = índice vazio (slider inerte).
   * CASCA: Fase 8b — o replay expõe o índice; até lá, ausente = desconhecido (o slider não trava por ele).
   */
  firstArchiveTMin?: number | null;
  /**
   * A apuração acabou (fase `after` do site, a partir de 26/10 06h00; DECISOES §86): sem o relógio de parede, sem "Ao
   * vivo" e sem o "agora" andando na trilha; o relógio mostra a hora dos dados e o chip diz "Encerrada". Quem monta passa
   * (a fase é do app, que `features/` não lê).
   */
  closed?: boolean;
}

interface DragState {
  id: number;
  touch: boolean;
  x0: number;
  y0: number;
  /** Mouse e caneta: desde o `pointerdown`; toque: só depois da intenção horizontal. */
  dragging: boolean;
  /** Último gesto pedido neste arraste (minuto, ou -1 = ao vivo): cada minuto novo vira um pedido. */
  lastKey: number | null;
}

interface Pending {
  g: Gesture | null;
  timer: ReturnType<typeof setTimeout> | null;
  /** performance.now() do último `scrubTo`. */
  last: number;
}

const pct = (t: number, w: TrackWindow) => `${(pos(t, w) * 100).toFixed(3)}%`;

function capture(el: Element, id: number): void {
  try {
    el.setPointerCapture(id);
  } catch {
    // ponteiro já solto (InvalidStateError): o arraste segue sem captura
  }
}
function release(el: Element, id: number): void {
  try {
    if (el.hasPointerCapture(id)) el.releasePointerCapture(id);
  } catch {
    // idem
  }
}

/**
 * Linha do tempo (§10.2, §10.4; FE `features/timeline/`): pílula de vidro com o relógio, a trilha (`role="slider"`,
 * ou `role="img"` com `features.replay` desligada), as horas e o estado ao vivo (`LiveChip` / `GoLiveButton`, 124 px).
 *
 * - Relógio: ao vivo com feed real, o de parede de Brasília com segundos (`fmtClockSec`, prefixo de dia fora de 25/10);
 *   no replay e na simulação, o instante exibido (`fmtTime`, sem segundos; em `--color-fg-3` enquanto carrega). Uma
 *   cópia invisível da outra forma reserva a largura: trocar entre ao vivo e replay não muda a trilha (AC-10.2-05).
 * - Trilha: janela de `trackWindow` (M15, congelada em 100%); disponível até `availTMin`; percorrido e cursor no
 *   instante exibido (ao vivo, em `availTMin`); ponto dos dados no relógio dos dados (`clockTMin`, §10.6.4: "ponto dos
 *   dados parado" quando o feed para), com `ping` a cada minuto novo de dados ao vivo; sem transição (bloco 5).
 * - Horas: densidade adaptativa com histerese (`markLevel`) e a guarda de sobreposição de `hourMarks` (AC-17.2-24).
 * - Gestos (§10.4): mouse e caneta capturam no `pointerdown`; toque só registra a origem e vira arraste com intenção
 *   horizontal (no `flow`, `touch-action: pan-y` deixa a rolagem vertical com o navegador); tocar e soltar vai ao ponto.
 *   Teclado: ← → PageUp PageDown (Shift ×10), Home, End; ↑ ↓ sem ação. Regra central em `gestureTarget`.
 * - Foco: clicar não foca a trilha (só a modalidade de teclado move foco, §10.4.4); o nó da trilha nunca é recriado
 *   (§15.2.2 regra 10); o "Voltar ao vivo" focado devolve o foco à trilha ao sumir (regra 4).
 * - TV: não renderiza (a `TvTicker` ocupa o lugar, §12.3).
 *
 * As ações partem do estado EM VIGOR (`cur`): uma segunda tecla antes do re-render parte do minuto já pedido.
 */
export function Timeline({ completeAtTMin, firstArchiveTMin, closed = false }: TimelineProps = {}) {
  const tl = useTimeline();
  const now = useNow();
  const cfg = useConfig();
  const view = useView();
  const { mode } = useLayout();
  const tv = mode === 'tv';

  const trackRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ w: number | null; level: MarkLevel }>({ w: null, level: 1 });
  const [hover, setHover] = useState<number | null>(null);
  const [preview, setPreview] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const [intro] = useState(() => {
    const first = !introShown;
    introShown = true;
    return first;
  });
  const drag = useRef<DragState | null>(null);
  const pending = useRef<Pending>({ g: null, timer: null, last: Number.NEGATIVE_INFINITY });
  const cur = useRef<TimelineClock>({ live: tl.live, clockTMin: tl.clockTMin, displayTMin: tl.displayTMin });
  const refocus = useRef(false);
  const firstComplete = useRef<number | null>(null);

  // Estado em vigor: o do sinal, salvo um pedido ainda na fila (o sinal só muda quando ele sair).
  cur.current =
    pending.current.g === null
      ? { live: tl.live, clockTMin: tl.clockTMin, displayTMin: tl.displayTMin }
      : { ...cur.current, clockTMin: tl.clockTMin };

  const simulation = cfg.mode === 'simulation';
  const replayOn = cfg.features.replay;
  const hasData = view !== null;
  const date = cfg.election_date;
  if (
    completeAtTMin === undefined &&
    firstComplete.current === null &&
    tl.live &&
    view?.national.status === 'complete'
  ) {
    firstComplete.current = view.instantTMin;
  }
  const win = trackWindow(tl.clockTMin, completeAtTMin !== undefined ? completeAtTMin : firstComplete.current);
  // encerrada: o "agora" não anda mais na trilha (ela vai só até o último dado)
  const avail = hasData ? availTMin(tl, now.tMin, !simulation && !closed) : T_OPEN;
  const inert = sliderInert({
    replayOn,
    hasData,
    nowTMin: now.tMin,
    clockTMin: tl.clockTMin,
    simulation,
    firstArchiveTMin,
  });
  const cursorT = !hasData ? T_OPEN : preview !== null ? preview : tl.live ? avail : tl.displayTMin;
  const cursor = Math.min(Math.max(cursorT, win.start), win.end);
  const geo = useRef({ win, avail, inert });
  geo.current = { win, avail, inert };

  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      if (!(w > 0)) return; // oculto (display: none) ou sem layout: fica a última medida
      setSize((prev) => (prev.w === w ? prev : { w, level: markLevel(w, prev.w === null ? null : prev.level) }));
    };
    // Com ResizeObserver, a 1ª medida vem do retorno dele (depois do layout do quadro, antes da pintura): medir aqui
    // forçava o layout da página inteira dentro da tarefa de montagem (~20% dela no perfil com a CPU 4×; TBT, DECISOES
    // §47). Só esta peça dos seis componentes do #90: o WebKit do CI travou com os seis juntos (§38).
    if (typeof ResizeObserver !== 'function') {
      measure();
      return;
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tv]);

  useLayoutEffect(
    () => () => {
      const p = pending.current;
      if (p.timer !== null) clearTimeout(p.timer);
      p.timer = null;
    },
    [],
  );

  // §15.2.2 regra 4: o "Voltar ao vivo" focado saiu do DOM; o foco volta à trilha (com a modalidade de teclado).
  useLayoutEffect(() => {
    if (!tl.live || !refocus.current) return;
    refocus.current = false;
    focusIfKeyboard(trackRef.current, { scroll: false });
  }, [tl.live]);

  if (tv) return null;

  // ── Pedidos (regra central de §10.4.1 já aplicada em `g`) ──
  function cancelPending(): void {
    const p = pending.current;
    if (p.timer !== null) clearTimeout(p.timer);
    p.timer = null;
    p.g = null;
  }

  function flush(): void {
    const p = pending.current;
    const g = p.g;
    cancelPending();
    setPreview(null);
    if (g === null || g.kind !== 'scrub') return;
    p.last = performance.now();
    scrubTo(g.tMin);
  }

  /** "Ao vivo" sai na hora (`force`: o `End` sempre chama `goLive`); replay no máximo a cada 250 ms. */
  function request(g: Gesture, force = false): void {
    const c = cur.current;
    if (g.kind === 'live') {
      const hadPending = pending.current.g !== null;
      cancelPending();
      if (hadPending) setPreview(null);
      if (force || !c.live || hadPending) goLive();
      cur.current = { ...c, live: true, displayTMin: c.clockTMin };
      return;
    }
    const p = pending.current;
    p.g = g;
    cur.current = { ...c, live: false, displayTMin: g.tMin };
    setPreview(g.tMin);
    if (p.timer !== null) return;
    const wait = SCRUB_MIN_INTERVAL_MS - (performance.now() - p.last);
    if (wait <= 0) flush();
    else p.timer = setTimeout(flush, wait);
  }

  // ── Ponteiro e toque (§10.4.2) ──
  function minuteAt(clientX: number): number | null {
    const el = trackRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (!(r.width > 0)) return null;
    const g = geo.current;
    return Math.min(minuteAtFraction((clientX - r.left) / r.width, g.win), g.avail);
  }

  function gestureAt(clientX: number): void {
    const x = minuteAt(clientX);
    if (x === null) return;
    const g = gestureTarget(x, cur.current.clockTMin);
    const key = g.kind === 'live' ? -1 : g.tMin;
    const d = drag.current;
    if (d) {
      if (d.lastKey === key) return;
      d.lastKey = key;
    }
    request(g);
  }

  function showHover(e: PointerEvent): void {
    if (e.pointerType !== 'mouse' || geo.current.inert) return;
    setHover(minuteAt(e.clientX));
  }

  const onPointerDown = (e: JSX.TargetedPointerEvent<HTMLDivElement>) => {
    if (geo.current.inert) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const touch = e.pointerType === 'touch';
    drag.current = { id: e.pointerId, touch, x0: e.clientX, y0: e.clientY, dragging: !touch, lastKey: null };
    if (touch) return; // não captura: a rolagem vertical continua do navegador
    capture(e.currentTarget, e.pointerId);
    setDragging(true);
    gestureAt(e.clientX);
    showHover(e);
  };

  const onPointerMove = (e: JSX.TargetedPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (d && d.id === e.pointerId) {
      if (!d.dragging) {
        const dx = Math.abs(e.clientX - d.x0);
        const dy = Math.abs(e.clientY - d.y0);
        if (dx > TOUCH_DRAG_PX && dx > dy) {
          d.dragging = true;
          capture(e.currentTarget, e.pointerId);
          setDragging(true);
        }
      }
      if (d.dragging) gestureAt(e.clientX);
    }
    showHover(e);
  };

  const endDrag = (e: JSX.TargetedPointerEvent<HTMLDivElement>, tap: boolean) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    drag.current = null;
    setDragging(false);
    release(e.currentTarget, e.pointerId);
    if (tap && d.touch && !d.dragging && Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < TAP_MAX_PX) {
      gestureAt(d.x0);
    }
    // soltar: o último minuto pedido sai na hora; o cursor encaixa no snapshot exibido (§10.5.3)
    if (pending.current.timer !== null) flush();
  };

  const onKeyDown = (e: JSX.TargetedKeyboardEvent<HTMLDivElement>) => {
    if (e.altKey || e.ctrlKey || e.metaKey || geo.current.inert) return;
    const a = keyAction(e.key, e.shiftKey);
    if (!a) return; // ↑ ↓ e as demais: a página rola (§10.4.3)
    e.preventDefault();
    request(keyGesture(a, cur.current, firstArchiveTMin), a.kind === 'end');
  };

  // ── Vista ──
  const shownT = hasData ? tl.displayTMin : T_OPEN;
  const dataFace = fmtTime(shownT, date);
  // encerrada: nem o relógio de parede nem a cópia dele (ao vivo e replay mostram a hora dos dados, largura igual)
  const wallFace = !simulation && !closed && now.ms > 0 ? fmtClockSec(now.ms, date) : null;
  const clockFace = tl.live && wallFace !== null ? wallFace : dataFace;
  const clockSizer = wallFace === null ? '' : clockFace === wallFace ? dataFace : wallFace;

  const liveMinute = simulation || closed || now.ms <= 0 ? shownT : now.tMin;
  const valueText = tl.live
    ? t(closed ? 'timeline.valuetext.closed' : 'timeline.valuetext.live', {
        HHhMM: fmtTime(Math.floor(liveMinute), date),
      })
    : t('timeline.valuetext.replay', { HHhMM: fmtTime(Math.floor(tl.displayTMin), date) });
  const valueMax = Math.max(T_OPEN, Math.floor(Math.min(avail, win.end)));
  const valueNow = Math.min(valueMax, Math.floor(cursor));

  const marks = hourMarks(win, size.w, size.level);
  const pingKey = tl.live ? `l${Math.floor(tl.clockTMin)}` : 'r';
  const state = !replayOn
    ? 'readonly'
    : !hasData
      ? 'empty'
      : tl.replayLoading
        ? 'loading'
        : !tl.live
          ? 'replay'
          : inert
            ? 'inert'
            : 'live';

  const trackParts = (
    <>
      <span class="timeline__rail" data-dataviz="" />
      <span class="timeline__avail" style={{ width: pct(avail, win) }} data-dataviz="" />
      {replayOn && <span class="timeline__done" style={{ width: pct(cursor, win) }} data-dataviz="" />}
      {hasData && (
        <span
          key={pingKey}
          class="timeline__live-point"
          style={{ left: pct(tl.clockTMin, win) }}
          data-ping={tl.live ? '' : undefined}
          data-dataviz=""
        />
      )}
      {replayOn && <span class="timeline__thumb" style={{ left: pct(cursor, win) }} data-dataviz="" />}
      {hover !== null && replayOn && !inert && (
        <span class="timeline__bubble" style={{ left: pct(hover, win) }} aria-hidden="true">
          {fmtTime(hover, date)}
        </span>
      )}
      <span class="timeline__hours" aria-hidden="true">
        {marks.hours.map((h) => (
          <span key={h} class="timeline__hour" style={{ left: pct(h * 60, win) }}>
            {fmtHourMark(h)}
          </span>
        ))}
      </span>
      {/* CASCA: Fase 8b — TimelinePins (§10.3): pinos de saliência 3 aqui, depois das horas, em ordem cronológica
          (Tab: trilha → pinos → estado ao vivo); o pointerdown do pino não inicia o arraste. */}
    </>
  );
  // Sem `features.replay` (config congelada: a variante nunca troca durante a sessão), só leitura: `role="img"` com o
  // nome de §10.2, sem slider, cursor, balão nem gestos.
  const track = replayOn ? (
    <div
      ref={trackRef}
      class="timeline__track"
      data-testid="timeline-track"
      data-dragging={dragging ? '' : undefined}
      role="slider"
      tabIndex={0}
      aria-label={t('timeline.aria')}
      aria-valuemin={T_OPEN}
      aria-valuemax={valueMax}
      aria-valuenow={valueNow}
      aria-valuetext={valueText}
      aria-disabled={inert ? 'true' : undefined}
      aria-busy={tl.replayLoading ? 'true' : undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={(e) => endDrag(e, true)}
      onPointerCancel={(e) => endDrag(e, false)}
      onPointerLeave={() => setHover(null)}
      onKeyDown={onKeyDown}
      onMouseDown={(e) => e.preventDefault()}
    >
      {trackParts}
    </div>
  ) : (
    <div
      ref={trackRef}
      class="timeline__track"
      data-testid="timeline-track"
      role="img"
      aria-label={t('timeline.readonly')}
    >
      {trackParts}
    </div>
  );

  return (
    <div
      class={`timeline surface-glass-pill${intro ? ' timeline--intro' : ''}`}
      data-testid="timeline"
      data-state={state}
      data-end={win.end}
      data-step={marks.step}
      data-level={size.level}
    >
      <div class="timeline__row">
        <time class="timeline__clock" aria-hidden="true" data-loading={tl.replayLoading ? '' : undefined}>
          <span class="timeline__clock-face">{clockFace}</span>
          <span class="timeline__clock-face timeline__clock-sizer">{clockSizer}</span>
        </time>
        {track}
        {/* PresenceCounter (§6.11, P2, flag `presence`): entre a trilha e o chip, só no desktop e com N > 0 (Fase 15). */}
        <span class="timeline__state">
          {tl.live || !replayOn ? (
            <LiveChip closed={closed} />
          ) : (
            <GoLiveButton
              closed={closed}
              onActivate={(hadFocus) => {
                refocus.current = hadFocus;
                cancelPending();
                setPreview(null);
              }}
            />
          )}
        </span>
      </div>
    </div>
  );
}
